import React, { useCallback, useEffect, useRef, useState } from 'react';

import ConfirmModal from '@components/shared/ConfirmModal';
import GlobalBarcodeInput from '@components/operator/GlobalBarcodeInput';
import OperatorVerificationModal from '@components/operator/qc/OperatorVerificationModal';
import OrderItemsTable from '@components/operator/qc/OrderItemsTable';
import ParcelItemsManagementModal from '@components/operator/qc/ParcelItemsManagementModal';
import ParcelsList from '@components/operator/qc/ParcelsList';
import SerialNumberModal from '@components/operator/qc/SerialNumberModal';
import VideoRecorder from '@components/operator/qc/VideoRecorder';
import VideoUploadQueueStatus from '@components/operator/qc/VideoUploadQueueStatus';
import { isDoParcelBarcode } from '@constants/specialBarcodes';
import { useBarcodeInput, useBarcodeInputContext } from '@contexts/operator/BarcodeInputContext';
import { QcProvider, useQc } from '@contexts/operator/QcContext';
import { useSingleNotification } from '@hooks/operator/useBackgroundJobNotification';
import HoldOrderModal from '@shared/orders/HoldOrderModal';
import ReprintLabelModal from '@shared/orders/ReprintLabelModal';
import soundEffects from '@utils/soundEffects';
import { Alert, Empty, Spin, Button, Modal, Input, Switch, Tooltip, Select, Tag, Space, Typography, message } from 'antd';
import printJS from 'print-js';
import { useTranslation } from 'react-i18next';
import { useBlocker, useNavigate, useSearchParams } from 'react-router-dom';

import { operatorApiFetch } from '@services/operator/apiService';
import { fulfillmentService } from '@services/operator/fulfillmentService';
import orderService from '@services/operator/orderService';

const { Text } = Typography;
const { Option } = Select;

function QcContent() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialFulfillmentNumber = searchParams.get('fulfillment_number');
  const hasAutoLoaded = useRef(false);

  const {
    operatorsVerified,
    verifyOperators,
    loading,
    error,
    errorInterpolations,
    operatorLoading,
    operatorError,
    qcOperator,
    parcelOperator,
    qcFlowState,
    qcFlowLabel,
    scanOrderByCode,
    fetchQcInfoByNumber, // 新增：直接透過編號獲取 QC 資訊
    scanBarcodeForPrinting,  // 新增：專門用於 AWAITING_RESCAN 狀態的列印觸發
    scanItemByCode,
    batchScanItem,
    removeSerialNumber,
    canTriggerQcFlowEvent,
    currentQcFulfillment,
    QCFlowStates,
    QCFlowEvents,
    parcels,
    currentParcelIndex,
    addNewParcel,
    switchToParcel,
    performParcel,
    updateParcelItems,
    clearAllParcels,
    videoSubtitle,
    uploadQueue, // 從 Context 取得（跨訂單共享）
    setVideoSplitter,
    resetToDefaultState,
    packingLockConflict,
    confirmPackingTakeover,
    cancelPackingTakeover,
    platformCheckFailed,
    confirmProceedWithoutPlatformCheck,
    cancelProceedWithoutPlatformCheck,
  } = useQc();

  // 列印失敗攔截 Modal
  const [holdModalVisible, setHoldModalVisible] = useState(false);
  const [holdingOrderId, setHoldingOrderId] = useState(null);
  const [holdingOrderName, setHoldingOrderName] = useState('');
  const [holdLoading, setHoldLoading] = useState(false);

  // 重印標籤 Modal（COMPLETED 狀態下使用，走跟 OrderShow 同一條 reprint flow）
  const [reprintModalVisible, setReprintModalVisible] = useState(false);
  const [reprinting, setReprinting] = useState(false);
  const [reprintJobId, setReprintJobId] = useState(null);

  // 重印 job 完成監聽：印 PDF + 不觸發 QC flow（已在 COMPLETED），不重新關閉 fulfillment。
  // 抽成子元件並在有 reprintJobId 時才掛載，避免跟 QcShipmentContext 內既有的
  // useSingleNotification(printJobId) 競爭同一個 mock store（兩個 hook 在同個 render 樹下
  // 跑，後者會把前者寫進 store 的 jobId 覆寫回 null）。
  const handleReprintCompleted = useCallback((data) => {
    const resultData = data?.data;
    const pdfUrl = resultData?.pdf_url || resultData?.logist_pdf_path;
    if (pdfUrl && typeof pdfUrl === 'string' && pdfUrl.startsWith('https://')) {
      try {
        printJS({
          printable: pdfUrl,
          type: 'pdf',
          showModal: true,
          modalMessage: t('operator.qc.print_label.loading_pdf', { defaultValue: '載入標籤中...' }),
          // PDF 載入 / 列印失敗：紅色 toast 配「重印失敗」（不能用 reprint_label_complete，
          // 否則「紅色×」配「重印完成」會語意衝突且使用者誤以為成功）
          onError: (err) => {
            console.error('[QC][reprint] print-js error:', err);
            message.error(t('shared.orders.detail.actions.reprint_label_failed', { defaultValue: '重印失敗' }));
          },
        });
        // 注意：printJS 不會 throw，這裡的 success 是 fire-and-forget。失敗只能透過 onError 反饋，
        // 所以不在此再發第二個 success（前一版會跟 onError 並發產生雙 toast）。
      } catch (err) {
        // 同步例外（極罕見）— 走錯誤路徑
        console.error('[QC][reprint] print-js exception:', err);
        message.error(t('shared.orders.detail.actions.reprint_label_failed', { defaultValue: '重印失敗' }));
      }
    } else {
      message.success(t('shared.orders.detail.actions.reprint_label_complete', { defaultValue: '重印完成' }));
    }
    setReprinting(false);
    setReprintJobId(null);
  }, [t]);

  const handleReprintFailed = useCallback((data) => {
    console.error('[QC][reprint] job failed:', data);
    message.error(t('shared.orders.detail.actions.reprint_label_failed', { defaultValue: '重印失敗' }));
    setReprinting(false);
    setReprintJobId(null);
  }, [t]);

  const handleReprintLabel = useCallback(async (fulfillmentId, quantity) => {
    try {
      setReprinting(true);
      const data = await fulfillmentService.printLabel(fulfillmentId, quantity, { reprint: true });
      if (data.success && data.data?.job_id) {
        setReprintJobId(data.data.job_id);
        message.info(t('shared.orders.detail.actions.reprint_label_started', { defaultValue: '重印標籤中...' }));
        setReprintModalVisible(false);
      } else if (data.success && data.data?.skipped) {
        // 合法 skip：source_none（倉庫自提無 label）/ source_custom 沒手動上傳託運單。
        // 不是錯誤，提示使用者該物流類型無需重印；不再 subscribe job、關閉 Modal。
        message.info(t('shared.orders.detail.actions.reprint_label_skipped', { defaultValue: '此物流類型無需列印標籤' }));
        setReprintModalVisible(false);
        setReprinting(false);
      } else {
        const errorMessage = data.message || data.error || 'shipment_failed';
        const interpolations = data.interpolations || {};
        message.error(t(`common.error.${errorMessage}`, { ...interpolations, defaultValue: errorMessage }));
        setReprinting(false);
      }
    } catch (err) {
      console.error('[QC] Reprint label failed:', err);
      message.error(t('common.error.operation_failed'));
      setReprinting(false);
    }
  }, [t]);

  const handleShowHoldModal = useCallback(() => {
    if (currentQcFulfillment?.orders?.length > 0) {
      const order = currentQcFulfillment.orders[0];
      setHoldingOrderId(order.id);
      setHoldingOrderName(order.name);
      setHoldModalVisible(true);
    }
  }, [currentQcFulfillment]);

  const handleHoldFromQc = useCallback(async (values) => {
    if (!holdingOrderId) return;
    try {
      setHoldLoading(true);
      const data = await orderService.hold(holdingOrderId, values);
      if (data.success) {
        message.success(t('operator.qc.print_label.hold_success'));
        setHoldModalVisible(false);
      } else {
        message.error(data.message || t('common.error.operation_failed'));
      }
    } catch (err) {
      console.error('[QC] Failed to hold order:', err);
      message.error(t('common.error.operation_failed'));
    } finally {
      setHoldLoading(false);
    }
  }, [holdingOrderId, t]);

  // ✅ Auto-load fulfillment if fulfillment_number is in URL
  useEffect(() => {
    if (operatorsVerified && initialFulfillmentNumber && !hasAutoLoaded.current) {
      hasAutoLoaded.current = true;
      fetchQcInfoByNumber(initialFulfillmentNumber);
    }
  }, [operatorsVerified, initialFulfillmentNumber, fetchQcInfoByNumber]);
  const { clear: clearBarcodeInput, focus: focusBarcodeInput } = useBarcodeInputContext();
  const videoRecorderRef = useRef(null);
  const parcelsListRef = useRef(null);
  const [shouldStartRecording, setShouldStartRecording] = useState(false);
  const [highlightedItemId, setHighlightedItemId] = useState(null);
  const [isBatchScanModalOpen, setIsBatchScanModalOpen] = useState(false);
  // { item, scanQuantity } — scanQuantity = 中/大單位條碼一次代表幾個 unit
  const [serialNumberItem, setSerialNumberItem] = useState(null);
  const [isParcelInProgress, setIsParcelInProgress] = useState(false);
  const [managingParcelIndex, setManagingParcelIndex] = useState(null);
  const [highlightedParcelIndex, setHighlightedParcelIndex] = useState(null);
  const [isClearAllModalOpen, setIsClearAllModalOpen] = useState(false);
  const [clearAllConfirmInput, setClearAllConfirmInput] = useState('');
  const [isClearingParcels, setIsClearingParcels] = useState(false);
  const [isOperatorModalOpen, setIsOperatorModalOpen] = useState(false);

  const scanOrderEvent = QCFlowEvents.SCAN_ORDER_BARCODE;

  const navigate = useNavigate();

  // 處理裝箱動作
  const handleDoParcel = useCallback(async () => {
    if (!currentQcFulfillment || !currentQcFulfillment.id) {
      soundEffects.playBuzzerError();
      return;
    }

    const currentParcel = parcels[currentParcelIndex];
    if (!currentParcel || !currentParcel.parcel_items || currentParcel.parcel_items.length === 0) {
      soundEffects.playBuzzerError();
      return;
    }

    if (isParcelInProgress) return;

    setIsParcelInProgress(true);

    try {
      // performParcel internally calls videoSplitter (registered above) on
      // backend success. We just handle the result.
      const parcelResult = await performParcel(currentQcFulfillment, currentParcelIndex);

      if (parcelResult.success) {
        soundEffects.playScan();
        addNewParcel();
      } else {
        soundEffects.playBuzzerError();
        console.error('[Index] Parcel failed:', parcelResult.error);
        message.error(
          t(`errors.${parcelResult.error}`, {
            defaultValue: parcelResult.error,
            ...(parcelResult.interpolations || {}),
          })
        );
      }
    } catch (err) {
      console.error('[Index] handleDoParcel error:', err);
      soundEffects.playBuzzerError();
    } finally {
      setIsParcelInProgress(false);
      focusBarcodeInput();
    }
  }, [
    isParcelInProgress, performParcel, currentParcelIndex, addNewParcel,
    currentQcFulfillment, parcels, focusBarcodeInput, t,
  ]);

  // 用 ref 抓最新的 fulfillment / uploadQueue / parcels,避免 60s 倒數的 callback 被 caller
  // 凍結在舊 closure (countdown context 只儲存第一次傳入的 callback)。
  const currentQcFulfillmentRef = useRef(currentQcFulfillment);
  const uploadQueueRef = useRef(uploadQueue);
  const parcelsRef = useRef(parcels);
  useEffect(() => { currentQcFulfillmentRef.current = currentQcFulfillment; });
  useEffect(() => { uploadQueueRef.current = uploadQueue; });
  useEffect(() => { parcelsRef.current = parcels; });

  // Video splitter: stops the current MediaRecorder session, returns the
  // just-closed session's meta, immediately starts a new session, and enqueues
  // a finalize task binding (sessionId, totalChunks) → parcelId.
  //
  // Registered into QcParcelContext so performParcel calls this on every
  // successful pack — including auto-pack paths (scanBarcodeForPrinting,
  // checkAndAutoPackIfComplete) that previously had no video handling.
  //
  // Uses refs for currentQcFulfillment and uploadQueue so the callback
  // identity is stable; we only register it once.
  const videoSplitter = useCallback(async (parcelId, parcelIndex) => {
    const recorder = videoRecorderRef.current;
    if (!recorder?.stopAndRestartRecording) return;

    const sessionMeta = await recorder.stopAndRestartRecording();
    const entityId = currentQcFulfillmentRef.current?.id;
    const queue = uploadQueueRef.current;
    if (!sessionMeta || !entityId || !queue || !parcelId) return;

    queue.enqueueFinalize({
      entityId,
      sessionId: sessionMeta.sessionId,
      totalChunks: sessionMeta.totalChunks,
      extra: {
        parcel_id: parcelId,
        video_index: parcelIndex + 1,
      },
    });
  }, []);

  useEffect(() => {
    setVideoSplitter(videoSplitter);
    return () => setVideoSplitter(null);
  }, [setVideoSplitter, videoSplitter]);

  // 結案後封箱影片的 finalize 流程:
  //  - 60s 倒數結束 / 手動結束 / 掃下一單,三者皆呼叫此 callback。
  //  - 必須在 fulfillment 還未被換掉前呼叫(掃下一單情境會 inline await)。
  //  - useCallback 用 [] deps 讓 callback identity 穩定;state/props 都從 ref 讀。
  //  - 不檢查 qcFlowState — 所有 caller 都已 guard 過 COMPLETED;這裡若 closure 帶到
  //    舊 state 反而會誤判 "已離開 COMPLETED" 而 skip stop,recording 卡死不停。
  const finishCompletedRecording = useCallback(async () => {
    const recorder = videoRecorderRef.current;
    if (!recorder?.stopRecording) return;

    // 一律先停錄影 — 即使後續 finalize 條件不滿足,也不能讓 camera/MediaRecorder 繼續跑。
    const meta = await recorder.stopRecording();

    const fulfillment = currentQcFulfillmentRef.current;
    const queue = uploadQueueRef.current;
    if (!fulfillment?.id || !meta || !queue) return;

    // 從 parcels 陣列推導 last packed parcel — 所有 pack 路徑(handleDoParcel /
    // scanBarcodeForPrinting / checkAndAutoPackIfComplete)都會把 parcel_id 寫進來,
    // 不必各路徑都記得同步一份 ref。closing video 綁在最後一個 packed parcel 之後。
    const list = parcelsRef.current || [];
    const lastPackedIndex = list.findLastIndex(p => p?.parcel_id);
    if (lastPackedIndex < 0) {
      throw new Error(
        '[finishCompletedRecording] no packed parcel found in COMPLETED state'
      );
    }

    // performParcel skip 掉最後一箱的 splitter,讓 session 留到此刻才停;
    // 因此 closing 直接接走 last parcel 的 video slot(N 箱 = N 支影片)。
    queue.enqueueFinalize({
      entityId:    fulfillment.id,
      sessionId:   meta.sessionId,
      totalChunks: meta.totalChunks,
      extra: {
        parcel_id:   list[lastPackedIndex].parcel_id,
        video_index: lastPackedIndex + 1,
      },
    });
  }, []);

  const handleSubmitBarcode = useCallback(async (rawValue) => {
    const code = String(rawValue || '').trim();
    if (!operatorsVerified || code.length === 0) {
      return;
    }

    // 清空輸入框的輔助函數
    const clearInput = () => {
      clearBarcodeInput();
    };

    // ⚠️ 優先檢查：是否為裝箱條碼（在任何狀態下都優先處理）
    if (isDoParcelBarcode(code)) {
      await handleDoParcel();
      clearInput();
      return;
    }

    // 根據當前狀態分發處理邏輯
    switch (qcFlowState) {
      case QCFlowStates.AWAITING_ORDER:
        // 等待訂單掃描
        if (canTriggerQcFlowEvent?.(scanOrderEvent)) {
          await scanOrderByCode(code);
        }
        clearInput();
        break;

      case QCFlowStates.COMPLETED:
        // 結案後掃下一單:先 finalize 前一單的封箱錄影,再 scan 新單。
        // 順序很重要 — scanOrderByCode 會切換 currentQcFulfillment,
        // 若先 scan 再 finalize,finalize 會抓到新單的 fulfillmentId。
        // finalize 一律先做(自身已 guard refs/state);是否進到下一單再受 canTriggerQcFlowEvent 限制。
        try {
          await finishCompletedRecording();
          if (canTriggerQcFlowEvent?.(scanOrderEvent)) {
            await scanOrderByCode(code);
          }
        } catch (err) {
          console.error('[QC] COMPLETED-branch finalize failed:', err);
          soundEffects.playBuzzerError();
        } finally {
          clearInput();
        }
        break;

      case QCFlowStates.SCANNING_ITEMS: {
        // Stuck-state recovery:state=SCANNING_ITEMS 但所有 items 已 shipped >= quantity 時,
        // 表示 auto-pack 沒成功 trigger ALL_ITEMS_COMPLETED (例如 backend 422 拒絕 pack)。
        // 死板 routing 到 scanItemByCode 會把出貨單號當商品條碼,丟 "找不到商品" 誤導訊息。
        // 改成 fallback 到 scanBarcodeForPrinting,讓 user 可以用出貨單號繼續結單流程。
        const allItemsCompleted = currentQcFulfillment?.items?.length > 0 &&
          currentQcFulfillment.items.every(item =>
            (item.shipped_quantity || 0) >= (item.quantity || 0)
          );
        if (allItemsCompleted) {
          const printResult = await scanBarcodeForPrinting(code);
          if (printResult !== null) {
            soundEffects.playScan();
          } else {
            soundEffects.playBuzzerError();
          }
          clearInput();
          break;
        }

        // 掃描商品
        const result = await scanItemByCode(code);


        // 如果需要序號，打開序號輸入 Modal
        if (result && result.needSerialNumber) {
          setSerialNumberItem({
            item: result.matchedItem,
            scanQuantity: result.scanQuantity || 1
          });
          clearInput();
          return;
        }

        if (result && result.scannedItemId) {
          // 播放成功音效
          soundEffects.playScan();

          // 高亮顯示並滾動到該項目
          setHighlightedItemId(result.scannedItemId);

          // 1 秒後清除高亮
          setTimeout(() => {
            setHighlightedItemId(null);
          }, 1000);
        } else {
          // 掃描失敗：播放錯誤音效
          soundEffects.playBuzzerError();
        }

        clearInput();
        break;
      }

      case QCFlowStates.AWAITING_RESCAN: {
        // 等待訂單複掃以進入列印流程

        // ✅ 調用專門的 scanBarcodeForPrinting 方法
        // 支援三種條碼格式：
        // 1. fulfillment.name (出貨單名稱)
        // 2. fulfillment.number (出貨單條碼)
        // 3. order.name (關聯訂單名稱)
        // 4. "-CONFIRM-" (特殊確認條碼)
        const printResult = await scanBarcodeForPrinting(code);

        if (printResult !== null) {
          // 成功觸發列印流程
          soundEffects.playScan();
        } else {
          // 條碼不匹配或發生錯誤（Context 已經顯示錯誤訊息）
          soundEffects.playBuzzerError();
        }

        clearInput();
        break;
      }

      default:
        // 其他狀態不處理條碼輸入
        clearInput();
        break;
    }
  }, [
    operatorsVerified,
    qcFlowState,
    QCFlowStates,
    currentQcFulfillment,
    canTriggerQcFlowEvent,
    scanOrderByCode,
    scanBarcodeForPrinting,  // 新增：AWAITING_RESCAN 狀態使用
    scanItemByCode,
    scanOrderEvent,
    handleDoParcel,
    clearBarcodeInput,
    finishCompletedRecording,
  ]);

  // 註冊條碼處理器到全域 BarcodeInput
  useBarcodeInput(handleSubmitBarcode, [handleSubmitBarcode]);

  useEffect(() => {
    const contentElement = document.querySelector('.operator-content');
    contentElement?.classList.add('operator-content--full-bleed');

    return () => {
      contentElement?.classList.remove('operator-content--full-bleed');
    };
  }, []);

  // 當訂單載入時，自動開始錄影
  useEffect(() => {
    if (currentQcFulfillment && currentQcFulfillment.id) {
      setShouldStartRecording(true);
    } else {
      setShouldStartRecording(false);
    }
  }, [currentQcFulfillment]);

  // ✅ 監聽視窗關閉/重整:QC 進行中 或 有 pending uploads 時警告。
  // QC 已 COMPLETED 且沒在上傳 → 視為合法離開,不提示也不 log。
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      const hasActiveUploads = uploadQueue.stats.uploading > 0 || uploadQueue.stats.pending > 0;
      const hasActiveSession = !!currentQcFulfillment?.id && qcFlowState !== QCFlowStates.COMPLETED;

      if (!hasActiveUploads && !hasActiveSession) {
        return undefined;
      }

      // 「使用者嘗試在 QC 過程中離開頁面」的稽核 log。
      // QC 已 COMPLETED 且 uploads 也清空時上面已 early return,跑到這裡
      // 一定有 hasActiveSession 或 hasActiveUploads,值得記錄。
      // 走 navigator.sendBeacon 才能保證關分頁/重刷瞬間 request 還是送得出去。
      if (currentQcFulfillment?.id && navigator.sendBeacon) {
        const url = `/api/operator/v1/fulfillments/${currentQcFulfillment.id}/log_action`;
        const leavePayload = JSON.stringify({
          history_action: 'attempt_leave_qc_page',
          details: {
            trigger: 'beforeunload',
            has_active_session: hasActiveSession,
            has_active_uploads: hasActiveUploads,
            uploading: uploadQueue.stats.uploading,
            pending: uploadQueue.stats.pending,
            failed: uploadQueue.stats.failed,
          },
        });
        navigator.sendBeacon(url, new Blob([leavePayload], { type: 'application/json' }));

        // 既有的「上傳中關頁」獨立稽核項保留 — 跟 attempt_leave_qc_page 並行 log,
        // 供報表區分「有 / 沒有」上傳中的離頁行為。
        if (hasActiveUploads) {
          const uploadPayload = JSON.stringify({
            history_action: 'attempt_close_with_uploading_videos',
            details: {
              uploading: uploadQueue.stats.uploading,
              pending: uploadQueue.stats.pending,
              completed: uploadQueue.stats.completed,
              failed: uploadQueue.stats.failed,
            },
          });
          navigator.sendBeacon(url, new Blob([uploadPayload], { type: 'application/json' }));
        }
      }

      const promptMessage = hasActiveUploads
        ? `現在離開會中斷目前的作業，還有 ${uploadQueue.stats.uploading + uploadQueue.stats.pending} 個項目尚未完成處理，可能會遺失。`
        : '現在離開會中斷目前的作業，已操作的資料可能無法正確儲存。';
      event.preventDefault();
      event.returnValue = promptMessage; // Chrome 要求設定 returnValue
      return promptMessage; // 其他瀏覽器
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [uploadQueue, currentQcFulfillment, qcFlowState, QCFlowStates.COMPLETED]);

  // ✅ 攔截 SPA router 導航:QC 進行中 或有 pending uploads 時跳 Modal 確認。
  // QC 已 COMPLETED 且沒在上傳 → 視為合法離開,不攔。
  // 確認離開後關閉 camera(呼叫 stopRecording 會釋放 MediaStream tracks)。
  const hasActiveUploadsForLeave = uploadQueue.stats.uploading > 0 || uploadQueue.stats.pending > 0;
  const hasActiveQcSession = !!currentQcFulfillment?.id && qcFlowState !== QCFlowStates.COMPLETED;
  const shouldWarnOnLeave = hasActiveQcSession || hasActiveUploadsForLeave;

  const navigationBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      shouldWarnOnLeave && currentLocation.pathname !== nextLocation.pathname
  );

  const blockerHandledRef = useRef(false);
  useEffect(() => {
    if (navigationBlocker.state !== 'blocked') {
      blockerHandledRef.current = false;
      return;
    }
    if (blockerHandledRef.current) return;
    blockerHandledRef.current = true;

    // 「使用者嘗試離開」的稽核 log — 攔到的當下發,即使 user 取消 Modal,
    // 「嘗試」本身就值得記錄(可看出操作員是否頻繁想中離)。
    if (currentQcFulfillment?.id) {
      operatorApiFetch(`/fulfillments/${currentQcFulfillment.id}/log_action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          history_action: 'attempt_leave_qc_page',
          details: {
            trigger: 'navigation_blocker',
            has_active_session: !!currentQcFulfillment?.id,
            has_active_uploads: hasActiveUploadsForLeave,
            uploading: uploadQueue.stats.uploading,
            pending: uploadQueue.stats.pending,
          },
        }),
      }).catch(err => {
        console.error('[Qc] attempt_leave_qc_page log failed:', err);
      });
    }

    const pendingCount = uploadQueue.stats.uploading + uploadQueue.stats.pending;
    Modal.confirm({
      title: t('operator.qc.leave_confirm.title', { defaultValue: '確定要離開嗎？' }),
      content: hasActiveUploadsForLeave
        ? t('operator.qc.leave_confirm.uploads', {
            count: pendingCount,
            defaultValue: `現在離開會中斷目前的作業，還有 ${pendingCount} 個項目尚未完成處理，可能會遺失。`,
          })
        : t('operator.qc.leave_confirm.session', {
            defaultValue: '現在離開會中斷目前的作業，已操作的資料可能無法正確儲存。',
          }),
      okText: t('common.button.leave', { defaultValue: '離開' }),
      cancelText: t('common.button.stay', { defaultValue: '留下' }),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await videoRecorderRef.current?.stopRecording();
        } catch (err) {
          console.error('[Qc] stopRecording on leave failed:', err);
        }
        navigationBlocker.proceed();
      },
      onCancel: () => navigationBlocker.reset(),
    });
  }, [navigationBlocker, hasActiveUploadsForLeave, uploadQueue, t, currentQcFulfillment]);

  // Unmount safety-net：跳過 useBlocker 直接 unmount 的情境（父層 re-render、error boundary 等）
  // stopRecording 內部對 !isRecording 的呼叫是 no-op（useChunkedVideoRecorder），重複呼叫安全
  useEffect(() => () => {
    videoRecorderRef.current?.stopRecording();
  }, []);

  const handleVerify = async (qcAccount, parcelAccount) => {
    try {
      await verifyOperators(qcAccount, parcelAccount);
      setIsOperatorModalOpen(false);
    } catch (err) {
      // Error handling is managed in the QC context
    }
  };

  const handleBatchScan = async (item, quantity, serialNumbers) => {
    const result = await batchScanItem(item, quantity, serialNumbers);

    if (result && result.itemId) {
      // 播放成功音效
      soundEffects.playScan();

      // 高亮顯示並滾動到該項目
      setHighlightedItemId(result.itemId);

      // 1 秒後清除高亮
      setTimeout(() => {
        setHighlightedItemId(null);
      }, 1000);
    } else {
      // 播放錯誤音效
      soundEffects.playBuzzerError();
    }
  };

  const handleSerialNumberConfirm = async (serialNumbers) => {
    if (!serialNumberItem) {
      return;
    }

    // 用序號再次呼叫 scanItemByCode
    // 注意：這裡需要用 matchedItem 的條碼，而不是原始掃描碼
    const matched = serialNumberItem.item;
    const code = matched.product?.barcode || matched.product?.sku;
    const result = await scanItemByCode(code, serialNumbers);


    if (result && result.scannedItemId) {
      // 播放成功音效
      soundEffects.playScan();

      // 高亮顯示並滾動到該項目
      setHighlightedItemId(result.scannedItemId);

      // 1 秒後清除高亮
      setTimeout(() => {
        setHighlightedItemId(null);
      }, 1000);
    } else {
      // 播放錯誤音效
      soundEffects.playBuzzerError();
    }

    // 關閉 Modal
    setSerialNumberItem(null);

    // Focus 回條碼輸入
    focusBarcodeInput();
  };

  const handleSerialNumberCancel = () => {
    // 直接關閉 Modal，不做任何處理
    // 因為在打開 Modal 之前，商品數量還沒有增加
    setSerialNumberItem(null);

    // Focus 回條碼輸入
    focusBarcodeInput();
  };

  const handleRemoveSerialNumber = (item, serialNumber, index) => {
    const result = removeSerialNumber(item, serialNumber, index);

    if (result && result.success) {
      // 播放成功音效
      soundEffects.playScan();

      // 高亮顯示並滾動到該項目
      setHighlightedItemId(item.id);

      // 1 秒後清除高亮
      setTimeout(() => {
        setHighlightedItemId(null);
      }, 1000);
    } else {
      // 播放錯誤音效
      soundEffects.playBuzzerError();
    }
  };

  const handleSerialNumberClick = (item, serialNumber, parcelIndex) => {
    // Scroll 到該 parcel
    if (parcelsListRef.current && parcelsListRef.current.scrollToParcel) {
      parcelsListRef.current.scrollToParcel(parcelIndex);
    }

    // 高亮該 parcel
    setHighlightedParcelIndex(parcelIndex);

    // 直接打開該 parcel 的管理 Modal
    setManagingParcelIndex(parcelIndex);

    // 3 秒後清除高亮（但 Modal 仍然開啟）
    setTimeout(() => {
      setHighlightedParcelIndex(null);
    }, 3000);
  };


  const handleManageParcel = (parcelIndex) => {
    setManagingParcelIndex(parcelIndex);
  };

  const handleParcelItemsManagementConfirm = async (updatedItems) => {
    if (managingParcelIndex === null) {
      return;
    }

    const result = await updateParcelItems(managingParcelIndex, updatedItems);

    if (result && result.success) {
      // 播放成功音效
      soundEffects.playScan();

      // 如果箱子被移除了，記錄日誌
      if (result.parcelRemoved) {
      }
    } else {
      // 播放錯誤音效
      soundEffects.playBuzzerError();
      // 顯示錯誤 toast，避免 silent failure（context 已 early-return 跳過 setParcels，
      // 操作者看不出 UI 沒被更新）。錯誤 code 走 common.error.* 翻譯，fallback 到 operation_failed。
      const errorCode = result?.error;
      message.error(
        errorCode
          ? t(`common.error.${errorCode}`, { defaultValue: t('common.error.operation_failed') })
          : t('common.error.operation_failed')
      );
    }

    // 關閉 Modal
    setManagingParcelIndex(null);

    // Focus 回條碼輸入
    focusBarcodeInput();
  };

  const handleParcelItemsManagementCancel = () => {
    setManagingParcelIndex(null);

    // Focus 回條碼輸入
    focusBarcodeInput();
  };

  // 處理清除所有 parcels - 打開確認 Modal
  const handleClearAllParcels = useCallback(() => {
    setIsClearAllModalOpen(true);
    setClearAllConfirmInput('');
  }, []);

  // 處理清除所有 parcels - 執行清除
  const handleClearAllParcelsConfirm = useCallback(async () => {
    setIsClearingParcels(true);

    try {
      const result = await clearAllParcels(currentQcFulfillment);

      if (result && result.success) {
        soundEffects.playScan();
      } else {
        console.error('[Index] ✗ Failed to clear parcels:', result?.error);
        soundEffects.playBuzzerError();
      }
    } finally {
      setIsClearingParcels(false);

      // 關閉 Modal 並清空輸入
      setIsClearAllModalOpen(false);
      setClearAllConfirmInput('');

      // Focus 回條碼輸入
      focusBarcodeInput();
    }
  }, [clearAllParcels, currentQcFulfillment]);

  // 處理清除所有 parcels - 取消
  const handleClearAllParcelsCancel = useCallback(() => {
    setIsClearAllModalOpen(false);
    setClearAllConfirmInput('');
    // 取消後 Focus 回條碼輸入
    focusBarcodeInput();
  }, []);

  // 處理重置按鈕（取消當前訂單，回到預設狀態）
  // ⚠️ 必須在條件判斷之前定義所有 hooks
  const handleReset = useCallback(() => {
    // 顯示確認對話框
    Modal.confirm({
      title: t('operator.qc.list.reset_confirm_title', { defaultValue: '確認重置' }),
      content: t('operator.qc.list.reset_confirm_message', {
        defaultValue: '確定要重置當前訂單嗎？所有未保存的掃描資料將會清除。'
      }),
      okText: t('common.button.confirm', { defaultValue: '確認' }),
      cancelText: t('common.button.cancel', { defaultValue: '取消' }),
      okButtonProps: { danger: true },
      onOk: async () => {
        // 停止影片錄製（如果正在錄製）
        if (videoRecorderRef.current && videoRecorderRef.current.stopRecording) {
          videoRecorderRef.current.stopRecording();
        }

        // 重置到預設狀態（會 await 後端 DELETE parcels）
        const result = await resetToDefaultState();

        if (result?.success) {
          soundEffects.playScan();
        } else {
          soundEffects.playBuzzerError();
        }

        // Focus 回條碼輸入
        focusBarcodeInput();
      },
      onCancel: () => {
        // 取消後 Focus 回條碼輸入
        focusBarcodeInput();
      }
    });
  }, [resetToDefaultState, t]);

  const qcName = qcOperator?.name || qcOperator?.username || qcOperator?.account || '--';
  const parcelName = parcelOperator?.name || parcelOperator?.username || parcelOperator?.account || '--';
  const operatorSummary = t('operator.qc.list.operators_info', {
    qc_operator: qcName,
    parcel_operator: parcelName,
  });
  const isLoadingContent = loading;
  // 如果 error 已經是完整的 i18n key (包含 '.')，直接使用；否則依序嘗試 operator.errors.* → common.error.*
  const errorInterpolationValues = errorInterpolations || {};
  const errorMessage = error
    ? (error.includes('.')
        ? t(error, { ...errorInterpolationValues, defaultValue: error })
        : t(`operator.errors.${error}`, {
            ...errorInterpolationValues,
            defaultValue: t(`common.error.${error}`, { ...errorInterpolationValues, defaultValue: error }),
          }))
    : null;

  // 格式化時間
  const formatCurrentTime = (date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  return (
    <div className="qc-page">
      <div className="qc-page__left">
        <header className="qc-page__left-header">
          {/* 三大塊橫向排列 */}
          <div className="qc-page__header-blocks">
            {/* 第一塊：出貨單名稱 + 重置按鈕 */}
            <div className="qc-page__header-block qc-page__order-block">
              {currentQcFulfillment ? (
                <>
                  {/* 第一行：標籤 + 操作按鈕組（重置 / 重印）*/}
                  <div className="qc-page__order-header">
                    <span className="qc-page__order-label">
                      {t('operator.qc.list.order_label', { defaultValue: '出貨單' })}:
                    </span>
                    {/* 按鈕組 — 作為一個整體放在右側，避免被 space-between 拆散 */}
                    <Space size={8}>
                      <Tooltip
                        title={
                          qcFlowState === QCFlowStates.COMPLETED
                            ? t('operator.qc.list.reset_disabled_completed', { defaultValue: '出貨已完成,無法重置' })
                            : ''
                        }
                      >
                        <Button
                          size="small"
                          danger
                          onClick={handleReset}
                          disabled={qcFlowState === QCFlowStates.COMPLETED}
                          className="qc-page__reset-button"
                        >
                          {t('common.button.reset', { defaultValue: '重置' })}
                        </Button>
                      </Tooltip>
                      {/* COMPLETED 狀態才顯示：印單成功後可重印標籤（走跟 OrderShow 同一條 reprint flow） */}
                      {qcFlowState === QCFlowStates.COMPLETED && (
                        <Button
                          size="small"
                          type="primary"
                          onClick={() => setReprintModalVisible(true)}
                          disabled={reprinting}
                          loading={reprinting}
                        >
                          {t('shared.orders.detail.actions.reprint_label', { defaultValue: '重印標籤' })}
                        </Button>
                      )}
                    </Space>
                  </div>
                  {/* 第二行：出貨單編號 */}
                  <div className="qc-page__order-name-row">
                    <span className="qc-page__order-name">{currentQcFulfillment.number}</span>
                  </div>
                  {/* 第三行：關聯訂單（如果有） */}
                  {currentQcFulfillment.orders && currentQcFulfillment.orders.length > 0 && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      訂單：{currentQcFulfillment.orders.map(o => o.name).join(', ')}
                    </div>
                  )}
                </>
              ) : (
                <div className="qc-page__order-placeholder">
                  <span className="qc-page__order-placeholder-text">
                    {t('operator.qc.list.no_order_loaded', { defaultValue: '請掃描出貨單' })}
                  </span>
                </div>
              )}
            </div>

            {/* 第二塊：QC 及包裝人員 */}
            <Tooltip title="點擊切換人員">
              <div
                className="qc-page__header-block qc-page__operators-block"
                onClick={() => setIsOperatorModalOpen(true)}
                style={{ cursor: 'pointer' }}
              >
                <div className="qc-page__operator-item">
                  <span className="qc-page__operator-label">
                    {t('operator.qc.list.qc_operator_label', { defaultValue: 'QC' })}:
                  </span>
                  <span className="qc-page__operator-name">{qcName}</span>
                </div>
                <div className="qc-page__operator-item">
                  <span className="qc-page__operator-label">
                    {t('operator.qc.list.parcel_operator_label', { defaultValue: '包裝' })}:
                  </span>
                  <span className="qc-page__operator-name">{parcelName}</span>
                </div>
              </div>
            </Tooltip>

            {/* 第三塊：物流類型 + 客代 */}
            <div className="qc-page__header-block qc-page__shipping-block">
              {currentQcFulfillment && currentQcFulfillment.shipping_type ? (
                <div className="qc-page__shipping-container">
                  {/* 第一行：物流類型名稱 */}
                  <div className="qc-page__shipping-row">
                    <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                      {currentQcFulfillment.shipping_type.name}
                    </Tag>
                  </div>

                </div>
              ) : (
                <div className="qc-page__shipping-placeholder">
                  <span className="qc-page__shipping-placeholder-text">
                    {t('operator.qc.list.no_shipping_info', { defaultValue: '無物流資訊' })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* 狀態指示器（獨立一行在最下方） */}
          <div className="qc-page__header-footer">
            <span className="qc-page__flow-state" data-state={qcFlowState}>
              {qcFlowLabel}
            </span>
          </div>
        </header>
        <div className="qc-page__left-content">
          {!isLoadingContent && errorMessage && (
            <Alert
              type="error"
              message={errorMessage}
              description={
                error === 'order_is_held' && errorInterpolations?.order_ids?.length > 0 ? (
                  <Space direction="vertical" size={4} style={{ marginTop: 8 }}>
                    {errorInterpolations.order_numbers?.map((num, idx) => (
                      <Button
                        key={errorInterpolations.order_ids[idx]}
                        type="link"
                        size="small"
                        style={{ padding: 0 }}
                        onClick={() => navigate(`/orders/${errorInterpolations.order_ids[idx]}`)}
                      >
                        {t('operator.errors.go_to_order', { defaultValue: '前往訂單' })}: {num}
                      </Button>
                    ))}
                  </Space>
                ) : currentQcFulfillment?.orders?.length > 0 ? (
                  <div style={{ marginTop: 8 }}>
                    <Button
                      type="primary"
                      danger
                      size="small"
                      onClick={handleShowHoldModal}
                    >
                      {t('operator.qc.print_label.hold_to_correct', { defaultValue: '攔截訂單以修正資訊' })}
                    </Button>
                  </div>
                ) : null
              }
              showIcon
              className="qc-page__alert"
            />
          )}
          {qcFlowState === QCFlowStates.AWAITING_RESCAN && (
            <Alert
              type="success"
              message={t('operator.qc.list.awaiting_rescan.title', { defaultValue: '所有商品已完成掃描' })}
              description={t('operator.qc.list.awaiting_rescan.description', { defaultValue: '請再次掃描訂單條碼以進入列印流程' })}
              showIcon
              className="qc-page__alert qc-page__alert--rescan"
              banner
            />
          )}
          {isLoadingContent ? (
            <div className="qc-page__spinner">
              <Spin size="large" />
            </div>
          ) : currentQcFulfillment && currentQcFulfillment.items ? (
            <OrderItemsTable
              orderItems={currentQcFulfillment.items}
              parcels={parcels}
              highlightedItemId={highlightedItemId}
              onBatchScan={handleBatchScan}
              onModalOpenChange={setIsBatchScanModalOpen}
              onSerialNumberClick={handleSerialNumberClick}
            />
          ) : (
            <div className="qc-page__placeholder">
              <Empty description={t('operator.qc.list.no_orders')} />
            </div>
          )}
        </div>
      </div>

      <div className="qc-page__right">
        <div className="qc-page__right-header">
          <GlobalBarcodeInput
            placeholder={t('operator.qc.list.barcode_input_placeholder', {
              defaultValue: '請掃描或輸入條碼',
            })}
            barcodeShortcuts={['DO_PACKING', 'CONFIRM_PRINT']}
          />
        </div>
        <div className="qc-page__right-content">
          <div className="qc-page__video-recorder">
            <VideoRecorder
              ref={videoRecorderRef}
              orderNumber={currentQcFulfillment?.name || ''}
              videoSubtitle={videoSubtitle}
              autoStart={shouldStartRecording}
              qcFlowState={qcFlowState}
              currentQcFulfillment={currentQcFulfillment}
              parcels={parcels}
              uploadQueue={uploadQueue}
              onStopAndFinalize={finishCompletedRecording}
            />
            <VideoUploadQueueStatus uploadQueue={uploadQueue} />
          </div>
          <div className="qc-page__parcels-list">
            <ParcelsList
              ref={parcelsListRef}
              parcels={parcels}
              currentParcelIndex={currentParcelIndex}
              highlightedParcelIndex={highlightedParcelIndex}
              onSwitchParcel={switchToParcel}
              onDoParcel={handleDoParcel}
              onManageParcel={handleManageParcel}
              onClearAllParcels={handleClearAllParcels}
              isParcelDisabled={isParcelInProgress || !currentQcFulfillment}
              isFulfillmentLocked={
                qcFlowState === QCFlowStates.COMPLETED ||
                ['shipped', 'canceled'].includes(currentQcFulfillment?.status)
              }
            />
          </div>
        </div>
      </div>

      <SerialNumberModal
        open={!!serialNumberItem}
        item={serialNumberItem?.item}
        scanQuantity={serialNumberItem?.scanQuantity || 1}
        // 跨 modal session 的 dedup:把這個商品(同 product_id)在所有 parcels 已記錄的 serials
        // 餵給 modal,讓它能在輸入時 inline 擋下已存在的序號 — 不要等到 backend 422 才知道。
        // 線上踩到的真實 case:user 連掃 20 次同商品,每個 modal session 是新的,
        // 不知道前面已輸過 "1",所以可以再輸一次 "1" → backend 422 → 被 silent swallow → stuck。
        existingSerialNumbers={
          serialNumberItem
            ? parcels.flatMap(p =>
                (p.parcel_items || [])
                  .filter(pi => pi.product_id === serialNumberItem.item?.product?.id)
                  .flatMap(pi => pi.serial_numbers || [])
              )
            : []
        }
        onConfirm={handleSerialNumberConfirm}
        onCancel={handleSerialNumberCancel}
      />

      <ParcelItemsManagementModal
        open={managingParcelIndex !== null}
        parcel={managingParcelIndex !== null ? parcels[managingParcelIndex] : null}
        parcelIndex={managingParcelIndex}
        onConfirm={handleParcelItemsManagementConfirm}
        onCancel={handleParcelItemsManagementCancel}
        loading={loading}
      />

      <Modal
        title={t('operator.qc.parcels_list.clear_all_confirm_title', { defaultValue: '確認清除所有箱子' })}
        open={isClearAllModalOpen}
        onOk={handleClearAllParcelsConfirm}
        onCancel={handleClearAllParcelsCancel}
        okText={isClearingParcels
          ? t('operator.qc.parcels_list.clearing', { defaultValue: '清除中...' })
          : t('common.button.confirm', { defaultValue: '確認清除' })
        }
        cancelText={t('common.button.cancel', { defaultValue: '取消' })}
        okButtonProps={{
          danger: true,
          disabled: clearAllConfirmInput !== 'clear' || isClearingParcels,
          loading: isClearingParcels
        }}
        cancelButtonProps={{
          disabled: isClearingParcels
        }}
        closable={!isClearingParcels}
        maskClosable={!isClearingParcels}
      >
        <div style={{ marginBottom: '16px' }}>
          {isClearingParcels ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Spin />
              <p style={{ marginTop: '12px', color: '#666' }}>
                {t('operator.qc.parcels_list.clearing_message', {
                  defaultValue: '正在清除所有箱子，請稍候...'
                })}
              </p>
            </div>
          ) : (
            <>
              <p>{t('operator.qc.parcels_list.clear_all_confirm_message', {
                defaultValue: '確定要清除所有箱子嗎？此操作無法復原。',
                count: parcels.length
              })}</p>
              <p style={{ marginTop: '12px', marginBottom: '8px', fontWeight: 500 }}>
                {t('operator.qc.parcels_list.clear_all_input_prompt', {
                  defaultValue: '請輸入 "clear" 來確認：'
                })}
              </p>
              <Input
                value={clearAllConfirmInput}
                onChange={(e) => setClearAllConfirmInput(e.target.value)}
                placeholder="clear"
                autoFocus
                disabled={isClearingParcels}
              />
            </>
          )}
        </div>
      </Modal>

      <OperatorVerificationModal
        open={isOperatorModalOpen}
        onCancel={() => setIsOperatorModalOpen(false)}
        onVerify={handleVerify}
        loading={operatorLoading}
        error={operatorError}
        initialQcAccount={qcOperator?.username || qcOperator?.account}
        initialParcelAccount={parcelOperator?.username || parcelOperator?.account}
      />

      <ConfirmModal
        open={!!packingLockConflict}
        onClose={cancelPackingTakeover}
        onConfirm={confirmPackingTakeover}
        title={t('operator.fulfillments.actions.takeover_packing_title')}
        description={t('operator.fulfillments.detail.qc_locked_by', {
          operator_name: packingLockConflict?.operatorName || ''
        })}
        confirmText="ok"
        danger={false}
        alertType="warning"
      />

      <Modal
        open={!!platformCheckFailed}
        title={t('operator.qc.platform_check.failed_title')}
        onOk={confirmProceedWithoutPlatformCheck}
        onCancel={cancelProceedWithoutPlatformCheck}
        okText={t('operator.qc.platform_check.proceed')}
        cancelText={t('common.button.cancel')}
        centered
      >
        {t('operator.qc.platform_check.failed_description')}
      </Modal>

      <HoldOrderModal
        visible={holdModalVisible}
        onCancel={() => setHoldModalVisible(false)}
        onConfirm={handleHoldFromQc}
        orderName={holdingOrderName}
        confirmLoading={holdLoading}
        initialReason="manual_intercept"
      />

      {/* 重印標籤 Modal — 走跟 OrderShow 同一條 reprint flow，後端 force_rebuild 軟刪重建 */}
      <ReprintLabelModal
        visible={reprintModalVisible}
        onCancel={() => setReprintModalVisible(false)}
        onConfirm={handleReprintLabel}
        order={currentQcFulfillment ? { fulfillments: [currentQcFulfillment], parcels } : undefined}
        loading={reprinting}
      />

      {/* 只在有 reprintJobId 時 mount，避免跟 QcShipmentContext.useSingleNotification(printJobId)
          競爭 mock store 的 jobId（後 mount 的 hook 會覆寫前者寫入的 jobId） */}
      {reprintJobId && (
        <ReprintJobListener
          jobId={reprintJobId}
          onCompleted={handleReprintCompleted}
          onFailed={handleReprintFailed}
        />
      )}
    </div>
  );
}

function ReprintJobListener({ jobId, onCompleted, onFailed }) {
  useSingleNotification(jobId, { onCompleted, onFailed });
  return null;
}

export default function QcIndex() {
  return (
    <QcProvider>
      <QcContent />
    </QcProvider>
  );
}
